'use client';
import FuseSearch from '@fuse/core/FuseSearch';
import { useRouter } from 'next/navigation';
import useNavigationItems from './hooks/useNavigationItems';

type NavigationSearchProps = {
	className?: string;
	variant?: 'basic' | 'full';
};

/**
 * The navigation search.
 * Supports searching navigation items by title AND searching invoices by number.
 * When the user types a term that doesn't match any nav item and presses Enter,
 * they are redirected to the Invoices page with the term pre-filled in the
 * Invoice Number filter.
 */
function NavigationSearch(props: NavigationSearchProps) {
	const { variant, className } = props;
	const { flattenData: navigation } = useNavigationItems();
	const router = useRouter();

	function handleSearch(searchTerm: string) {
		// Pre-populate the invoice number filter in sessionStorage so the
		// Invoices page picks it up on mount and fires the search immediately.
		try {
			const existing = JSON.parse(sessionStorage.getItem('invoiceFilters') || '{}');
			sessionStorage.setItem('invoiceFilters', JSON.stringify({
				...existing,
				invoiceNumber: searchTerm,
				offset: 0,
			}));
		} catch {
			// sessionStorage not available (SSR) — navigate anyway
		}
		router.push(`/invoices?invoiceNumber=${encodeURIComponent(searchTerm)}`);
	}

	function handleSearchChange(newValue: string) {
		if (typeof window !== 'undefined') {
			window.dispatchEvent(new CustomEvent('global-search-change', { detail: newValue }));
		}
	}

	return (
		<FuseSearch
			className={className}
			variant={variant}
			navigation={navigation}
			onSearch={handleSearch}
			onSearchChange={handleSearchChange}
		/>
	);
}

export default NavigationSearch;
